import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import type { EChartsOption } from 'echarts';
import {
  ChartConfig,
  ServiceCallDistributionChartConfigResolver,
} from '../../resolvers/service-call-distribution-chart-config.resolver';
import { ServiceMetricTypes } from '../../types/service-insight-types';

export interface ServiceCallDistributionItem {
  key: string;
  label: string;
  value: number;
  totalLocations: number;
  color: string;
}

@Component({
    selector: 'service-call-distribution-chart',
    templateUrl: './service-call-distribution-chart.component.html',
    styleUrls: ['./service-call-distribution-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ServiceCallDistributionChartComponent implements OnChanges, OnDestroy {
  @Input() items: ServiceCallDistributionItem[] = [];
  @Input() loading = false;
  @Input() serviceMetricType: ServiceMetricTypes = ServiceMetricTypes.Calls; 

  chartOptions: EChartsOption = {};

  /** Resolved config (categories + unit) for the current division/metric-type combination. */
  chartConfig: ChartConfig | undefined;

  private echartsInstance: any;
  private legendSelectedState: Record<string, boolean> = {};
  private resizeObserver: ResizeObserver | undefined;

  constructor(
    private readonly cdr: ChangeDetectorRef, 
    private _configResolver: ServiceCallDistributionChartConfigResolver,
  ) {}

  get hasData(): boolean {
    return this.normalizedItems.some(item => item.value > 0);
  }

  /** Merges the incoming items against the resolved category list so that
   *  missing categories default to value 0 and order is always preserved. */
  get normalizedItems(): ServiceCallDistributionItem[] {
    return (this.chartConfig?.categories ?? []).map(cat => {
      const found = this.items.find(i => i.key === cat.key);
      return {
        key: cat.key,
        label: found?.label ?? cat.label,
        value: found != null ? found.value : 0,
        totalLocations: found?.totalLocations ?? 0,
        color: cat.color
      };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceMetricType'] || !this.chartConfig) {
      this.chartConfig = this._configResolver.resolve(this.serviceMetricType);
    }

    if (changes['items'] || changes['serviceMetricType']) {
      // Reset legend toggles whenever new data arrives.
      this.legendSelectedState = {};
      this.buildChartOptions();
    }
  }

  onChartInit(chart: any): void {
    this.echartsInstance = chart;

    // Repaint when the host element is resized (e.g. sidebar open/close, window resize)
    this.resizeObserver = new ResizeObserver(() => chart.resize());
    this.resizeObserver.observe(chart.getDom());

    chart.on('legendselectchanged', (event: { selected: Record<string, boolean> }) => {
      this.legendSelectedState = event.selected;
      this.updateSeriesBorderRadius();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private buildChartOptions(): void {
    const normalizedItems = this.normalizedItems;
    const series          = this.buildSeries(normalizedItems, this.legendSelectedState);
    const totalCalls      = normalizedItems.reduce((sum, i) => sum + i.value, 0);
    const unit            = this.chartConfig?.unit ?? 'calls';

    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        extraCssText: 'box-shadow:none;',
        position: (point: number[], _params: any, dom: any, rect: any, size: any): [number, number] => {
          const tw       = Math.min((dom as HTMLElement)?.offsetWidth || 312, size.viewSize[0] - 16);
          const cursorX  = point[0];
          const idealX   = cursorX - tw / 2;
          const clampedX = Math.max(0, Math.min(idealX, size.viewSize[0] - tw));

          // Keep the triangle pointing at the cursor even when the box is clamped
          const trianglePx = Math.max(16, Math.min(cursorX - clampedX, tw - 16));
          const triangleEl = (dom as HTMLElement)?.querySelector?.('[data-triangle]') as HTMLElement | null;
          if (triangleEl) {
            triangleEl.style.left      = `${trianglePx}px`;
            triangleEl.style.transform = 'translateX(-50%)';
          }

          const y = rect ? rect.y + rect.height + 10 : point[1] + 10;
          return [clampedX, y];
        },
        formatter: (params: any): string => {
          if (!params.value) {
            return '';
          }
          const value      = (params.value as number).toLocaleString('en-US');
          const locations  = ((params.data?.totalLocations ?? 0) as number).toLocaleString('en-US');
          const color      = params.color as string;
          const percent        = totalCalls > 0
            ? ((params.value as number / totalCalls) * 100).toFixed(2)
            : '0.00';
          const bg = '#F0F3F5';
          return `
            <div style="max-width: 312px; width: max-content; background: ${bg}; border-radius: 4px;
                        padding: 12px 16px 8px 16px; box-sizing: border-box; display: flex;
                        flex-direction: column; gap: 4px; box-shadow: 0 2px 6px 2px rgba(20, 23, 26, 0.08),0 1px 2px 0 rgba(20, 23, 26, 0.15);
                        position: relative;">

              <div data-triangle style="position:absolute; top:-8px; left:50%; transform: translateX(-50%);
                          width: 0; height: 0; border-left: 8px solid transparent;
                          border-right:8px solid transparent; border-bottom: 8px solid ${bg};"></div>

              <div style="display: flex; align-items: center; gap: 8px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                  <circle cx="7.5" cy="7.5" r="7.5" fill="${color}"/>
                </svg>
                <span style="color: #14171A; font-size: 14px; font-weight: 500; line-height: 20px;">${params.seriesName}</span>
              </div>

              <div style="font-size: 14px; color: #363B40; line-height: 20px;">
                <span style="color: #363B40; font-weight: 600">${percent}%</span> of total ${unit}
              </div>

              <div style="font-size: 14px; font-weight: 400; color: #5B6065; line-height: 20px;">
                ${value} across ${locations} locations</span>
              </div>
            </div>
          `;
        }
      },
      legend: {
        type: 'scroll',
        bottom: 0,
        left: 0,
        orient: 'horizontal',
        itemGap: 24,
        icon: 'circle',
        itemWidth: 14,
        itemHeight: 14,
        align: 'left',
        textStyle: {
          fontSize: 12,
          color: '#363B40',
          padding: [0, 0, 0, 2]
        },
        data: this.normalizedItems.filter(i => i.value > 0).map(i => i.label) as string[]
      },
      grid: {
        left: 0,
        right: 0,
        top: 4,
        bottom: 32,
        containLabel: false
      },
      xAxis: {
        type: 'value',
        show: false,
        min: 0,
        max: totalCalls,
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false }
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false }
      },
      series: series as EChartsOption['series']
    };
  }

  private buildSeries(
    items: ServiceCallDistributionItem[],
    selectedState: Record<string, boolean>
  ): object[] {
    const visibleNonZero = items.filter(
      item => item.value > 0 && selectedState[item.label] !== false
    );

    const firstKey = visibleNonZero.length > 0 ? visibleNonZero[0].key : null;
    const lastKey  = visibleNonZero.length > 0 ? visibleNonZero[visibleNonZero.length - 1].key : null;

    return items.map(item => ({
      name: item.label,
      type: 'bar',
      stack: 'serviceCalls',
      barWidth: 24,
      data: [{ value: item.value, totalLocations: item.totalLocations }],
      itemStyle: {
        color: item.color,
        borderRadius: this.computeBorderRadius(item.key === firstKey, item.key === lastKey)
      }
    }));
  }

  /**
   * Returns the ECharts borderRadius tuple for a horizontal bar segment.
   * Order: [top-left, top-right, bottom-right, bottom-left]
   * – "left" visual edge  → top-left + bottom-left  → indices [0] and [3]
   * – "right" visual edge → top-right + bottom-right → indices [1] and [2]
   */
  private computeBorderRadius(isFirst: boolean, isLast: boolean): number[] {
    if (isFirst && isLast) return [4, 4, 4, 4];
    if (isFirst)           return [4, 0, 0, 4];
    if (isLast)            return [0, 4, 4, 0];
    return [0, 0, 0, 0];
  }

  /** Called after a legend toggle to recompute which segments get rounded ends. */
  private updateSeriesBorderRadius(): void {
    if (!this.echartsInstance) {
      return;
    }
    const series = this.buildSeries(this.normalizedItems, this.legendSelectedState);
    const visibleTotal = this.normalizedItems
      .filter(item => item.value > 0 && this.legendSelectedState[item.label] !== false)
      .reduce((sum, item) => sum + item.value, 0);
    this.echartsInstance.setOption({ series, xAxis: { max: visibleTotal } });
  }
}
